import type { GetPermissionGroupsParams } from '@kizen/api/permission-group';
import type { GetRolesParams } from '@kizen/api/role';
import type { GetTeamMembersParams } from '@kizen/api/team-member';
import type { PropsWithChildren } from 'react';

import { createContext, useContext, useState } from 'react';

type PermissionGroupSort = Exclude<
  GetPermissionGroupsParams['ordering'],
  undefined
>;
type RoleSort = Exclude<GetRolesParams['ordering'], undefined>;
type TeamMemberSort = Exclude<GetTeamMembersParams['ordering'], undefined>;

type TableState<S extends string = string> = {
  search: string;
  sort: S;
  setSearch(value: string): void;
  setSort(value: S): void;
};

export type SettingsState = {
  permissionGroups: TableState<PermissionGroupSort>;
  roles: TableState<RoleSort>;
  teamMembers: TableState<TeamMemberSort>;
};

type SettingsContext = ReturnType<typeof useSettings>;

const INITIAL_STATE: SettingsContext = {
  permissionGroups: {
    search: '',
    sort: 'name',
    setSearch() {},
    setSort() {},
  },
  roles: {
    search: '',
    sort: 'name',
    setSearch() {},
    setSort() {},
  },
  teamMembers: {
    search: '',
    sort: 'full_name',
    setSearch() {},
    setSort() {},
  },
};

const SettingsContext = createContext<SettingsContext>(INITIAL_STATE);

const useTableState = <S extends string>(
  init: Partial<Pick<TableState<S>, 'search' | 'sort'>> = {}
) => {
  const [search, setSearch] = useState(init.search);
  const [sort, setSort] = useState(init.sort);

  return {
    search,
    sort,
    setSearch,
    setSort,
  };
};

const useSettings = () => {
  const permissionGroups = useTableState<PermissionGroupSort>({ sort: 'name' });
  const roles = useTableState<RoleSort>({ sort: 'name' });
  const teamMembers = useTableState<TeamMemberSort>({ sort: 'full_name' });

  return {
    permissionGroups,
    roles,
    teamMembers,
  };
};

export const SettingsProvider = ({ children }: PropsWithChildren) => {
  const ctx = useSettings();

  return (
    <SettingsContext.Provider value={ctx}>{children}</SettingsContext.Provider>
  );
};

export const useSettingsContext = (): SettingsContext => {
  return useContext(SettingsContext);
};
